import { create } from 'zustand';
import { startAssistTurn, continueAssistTurn, type AssistConfirm } from '../api/assist';
import {
  listAiConversations, getAiConversation, renameAiConversation,
  deleteAiConversation, listAiModelOptions,
  type AiConversationSummary,
} from '../api/assist-conversations';
import { getAiAgent, type AiAgent } from '../api/assist-agents';
import { isAiModelAlias, type AiModelAlias } from '@workchat/shared-types';

/**
 * aiChatStore — AI 대화의 **내용** (설계 판 B).
 *
 * ★★★ **왜 컴포넌트가 아니라 store 인가.** 설계 §9-12 는 「패널을 언마운트하면 대화가
 *    사라진다 → 항상 마운트하고 표시만 CSS 로 가른다」고 적었다(오피스 챗 카드의 계약).
 *    그 계약은 **상태를 컴포넌트가 들고 있을 때**의 이야기다. 대화를 store 로 올리면
 *    패널을 닫아 언마운트해도 대화가 그대로 있고, **스트리밍도 끊기지 않는다** —
 *    같은 목적을 더 적은 제약으로 이룬다.
 * ★★ 그래서 패널과 AI 메뉴가 **같은 대화**를 본다. 둘이 각자 갖고 있으면 「패널에서 묻다가
 *    전체로 넘어갔더니 처음부터」가 된다.
 *
 * ★★★ **판 C 부터 대화가 남는다.** 새로고침해도, 기기를 옮겨도 이어진다 — 표는
 *    `ai_conversations`·`ai_conversation_messages` 다.
 * ★★★ **앞 맥락은 더 이상 화면이 보내지 않는다.** 대화 id 만 주고 서버가 제 DB 에서 읽는다 —
 *    전에는 사용자가 「AI 가 이렇게 말했다」를 지어내 보낼 수 있었다.
 */

export type AiRole = 'user' | 'assistant';

export interface AiMessage {
  id: string;
  role: AiRole;
  /** 스트리밍 중에는 지금까지 받은 만큼. */
  text: string;
  /** 이 말풍선이 실패로 끝났나 — 실패는 «답»이 아니라 «안내»로 그린다. */
  failed?: boolean;
  /**
   * 이 질문에 붙였던 파일 이름들 (2026-09-10) — **보여 주기 위한 것**이다.
   * 내용은 서버가 읽어 프롬프트에 실었고, 화면은 「무엇을 붙여 물었는지」만 남긴다.
   */
  files?: string[];
  /**
   * 쓰기 도구가 **실행 전에** 멈추고 묻는 중 (P1-#18).
   *
   * ★ 이 말풍선은 아직 «답»이 아니다 — 카드를 그리고 사용자의 승인을 기다린다.
   *   승인하면 같은 말풍선에 답이 이어 붙는다(새 말풍선을 만들지 않는다 — 한 질문 한 답).
   */
  confirm?: AssistConfirm;
}

interface AiChatState {
  /** 지금 이어 가는 대화. 아직 한 번도 안 물었으면 null(첫 턴에서 서버가 만들어 준다). */
  conversationId: string | null;
  /** 좌측 이력 — 「오늘 · 어제 · 지난 7일」로 나누는 것은 화면이 한다. */
  conversations: AiConversationSummary[];
  /** 이 배포가 고를 수 있는 모델 별칭(관문 G2). **비면 화면이 고르기를 안 그린다.** */
  modelAliases: AiModelAlias[];
  /** 판 K — 회사가 지정한 연결들. `null` = 못 읽었다(고르기를 안 그린다). */
  modelChoices: Array<{ key: string; label: string }> | null;
  /** 사용자가 이 대화에서 고른 연결 열쇠. `null` 이면 회사 기본으로 간다. */
  choiceKey: string | null;
  /** 지금 고른 별칭. 안 골랐으면 null — 그러면 배포의 전역 선택이 답한다. */
  model: AiModelAlias | null;
  /**
   * 이 대화를 이끄는 에이전트(판 E). null = 지금까지처럼 «그냥» 묻는다.
   * ★★★ **대화 하나에 하나다.** 다른 것을 고르면 새 대화가 시작된다 — 한 대화 안에서 역할이
   *    바뀌면 앞뒤 답이 왜 다른지 아무도 설명할 수 없다.
   */
  agent: AiAgent | null;
  listLoading: boolean;
  messages: AiMessage[];
  /** 지금 답을 받고 있나. */
  streaming: boolean;
  /** 보냈지만 아직 첫 글자가 안 온 상태 — 「생각 중」을 띄운다. */
  waiting: boolean;
  /** 중단 손잡이. 스트리밍 중에만 있다. */
  abort: (() => void) | null;

  /** 물어본다. 붙임 파일은 **이미 올라간 것의 id** 만 넘긴다(서버가 읽는다). */
  send: (text: string, attachments?: ReadonlyArray<{ fileId: string; name: string }>) => void;
  stop: () => void;
  /**
   * 승인 카드에 답한다 (P1-#18).
   *
   * ★ 답은 **같은 말풍선**에 이어 붙는다 — 한 질문 한 답이라는 화면의 약속을 지킨다.
   * ★ 거절도 정상 흐름이다 — 모델이 「알겠습니다」로 마무리한다.
   */
  resolveConfirm: (messageId: string, decision: 'approve' | 'deny') => void;
  /** 새 대화 — 화면만 비운다. 지난 대화는 목록에 그대로 남는다. */
  newConversation: () => void;
  /** 목록 새로 읽기. 실패는 조용히 넘어간다(이력은 덤이고 대화는 계속된다). */
  loadList: () => Promise<void>;
  /** 고를 수 있는 별칭 읽기. 실패·빈 목록이면 고르기가 안 뜬다. */
  loadModels: () => Promise<void>;
  setChoiceKey: (k: string | null) => void;
  setModel: (m: AiModelAlias | null) => void;
  /** 에이전트를 고른다 — 이미 오간 말이 있으면 **새 대화**로 넘어간다. */
  setAgent: (a: AiAgent | null) => void;
  /** 지난 대화 열기. */
  open: (id: string) => Promise<void>;
  /** ⚠ 되돌릴 수 없다 — 부르는 쪽이 **먼저** 확인을 받는다. */
  remove: (id: string) => Promise<void>;
  /** 대화 이름 바꾸기 — 목록의 그 줄만 갈아 끼운다(다시 불러오지 않는다). */
  rename: (id: string, title: string) => Promise<void>;
}

let seq = 0;
const nextId = (): string => `m${++seq}-${Date.now()}`;

export const useAiChatStore = create<AiChatState>((set, get) => ({
  conversationId: null,
  conversations: [],
  modelAliases: [],
  modelChoices: null,
  choiceKey: null,
  model: null,
  agent: null,
  listLoading: false,
  messages: [],
  streaming: false,
  waiting: false,
  abort: null,

  resolveConfirm: (messageId, decision) => {
    const msg = get().messages.find((m) => m.id === messageId);
    const c = msg?.confirm;
    if (!c) return;
    /* 카드를 먼저 내린다 — 두 번 누르는 것을 막고, 「보내는 중」이 보이게 한다. */
    const patch = (fn: (m: AiMessage) => AiMessage): void => set((st) => ({
      messages: st.messages.map((m) => (m.id === messageId ? fn(m) : m)),
    }));
    patch((m) => ({ ...m, confirm: undefined }));
    set({ streaming: true, waiting: true });
    void continueAssistTurn(
      {
        continuationId: c.continuationId,
        confirmToken: c.confirmToken,
        decision,
        ...(get().conversationId ? { conversationId: get().conversationId! } : {}),
      },
      {
        onDelta: (chunk) => { set({ waiting: false }); patch((m) => ({ ...m, text: m.text + chunk })); },
        onComplete: (full) => {
          patch((m) => ({ ...m, text: full }));
          set({ streaming: false, waiting: false });
          void get().loadList();
        },
        onError: (message) => {
          patch((m) => ({ ...m, text: message, failed: true }));
          set({ streaming: false, waiting: false });
        },
      },
    );
  },

  send: (raw, attachments) => {
    const text = raw.trim();
    if (!text || get().streaming) return;

    const files = (attachments ?? []).map((a) => a.name);
    const mine: AiMessage = {
      id: nextId(), role: 'user', text,
      ...(files.length > 0 ? { files } : {}),
    };
    const answer: AiMessage = { id: nextId(), role: 'assistant', text: '' };
    set((s) => ({
      messages: [...s.messages, mine, answer],
      streaming: true,
      waiting: true,
    }));

    /** 답 말풍선 한 칸만 고친다 — 다른 말풍선은 손대지 않는다. */
    const patch = (fn: (m: AiMessage) => AiMessage): void => {
      set((s) => ({ messages: s.messages.map((m) => (m.id === answer.id ? fn(m) : m)) }));
    };

    const done = (): void => set({ streaming: false, waiting: false, abort: null });

    /* ★ 이번 턴이 «새 대화»였나 — 실패하면 서버가 그 빈 대화를 지우므로 화면도 id 를 놓아야
         한다. 안 놓으면 다시 물었을 때 «없는 대화» 를 가리켜 404 가 난다. */
    const wasNew = !get().conversationId;

    const cancel = startAssistTurn(
      {
        text,
        conversationId: get().conversationId ?? undefined,
        model: get().model ?? undefined,
        choiceKey: get().choiceKey ?? undefined,
        agentId: get().agent?.id ?? undefined,
        /* ★ id 만 보낸다 — 내용은 서버가 읽는다(권한도 서버가 다시 판정한다). */
        ...(attachments?.length
          ? { attachments: attachments.map((a) => ({ fileId: a.fileId, name: a.name })) }
          : {}),
      },
      {
        /* 새 대화였으면 여기서 id 를 받는다 — 다음 턴부터 그 대화로 이어 간다. */
        onConversation: (id, agentId) => {
          if (id) set({ conversationId: id });
          /* ★★★ **서버가 «실제로 붙은 것»을 말한다** — 고른 에이전트가 그새 중지됐으면
                 화면도 놓는다. 안 그러면 이름은 떠 있는데 답은 그 역할이 아니다. */
          if (!agentId && get().agent) set({ agent: null });
        },
        onPing: () => set({ waiting: true }),
        onDelta: (chunk) => {
          set({ waiting: false });
          patch((m) => ({ ...m, text: m.text + chunk }));
        },
        /* ★ 최종 글로 «갈아 끼운다» — 조각을 이어 붙인 것과 다를 수 있다(마스킹이 풀린 판이다). */
        onComplete: (full) => {
          patch((m) => ({ ...m, text: full }));
          done();
          /* 목록의 제목·차례가 바뀌었을 수 있다 — 조용히 다시 읽는다. */
          void get().loadList();
        },
        /* ★★★ 승인이 필요하다 — 말풍선에 카드를 달고 **기다린다**(실패가 아니다). */
        onConfirm: (c) => {
          patch((m) => ({ ...m, confirm: c }));
          done();
        },
        onError: (message, cancelled) => {
          if (wasNew) set({ conversationId: null });
          patch((m) => {
            /* 중단은 실패가 아니다 — 여태 받은 글은 그대로 두고, 아무것도 못 받았을 때만 말한다. */
            if (cancelled) return m.text ? m : { ...m, text: '중단했습니다.', failed: true };
            return { ...m, text: message, failed: true };
          });
          done();
        },
      },
    );
    set({ abort: cancel });
  },

  stop: () => {
    get().abort?.();
    set({ streaming: false, waiting: false, abort: null });
  },

  newConversation: () => {
    get().abort?.();
    /* ★ 고른 에이전트는 **남긴다** — 「새 대화」는 대화를 비우는 것이지 에이전트를 놓는 것이
         아니다(그 에이전트로 여러 번 묻는 것이 보통이다). 놓으려면 칩의 ×를 누른다. */
    set({ conversationId: null, messages: [], streaming: false, waiting: false, abort: null });
  },

  loadList: async () => {
    set({ listLoading: true });
    try {
      set({ conversations: await listAiConversations() });
    } catch {
      /* 이력은 덤이다 — 못 읽어도 대화는 계속된다. */
    } finally {
      set({ listLoading: false });
    }
  },

  loadModels: async () => {
    try {
      const opt = await listAiModelOptions();
      const aliases = opt.aliases.filter(isAiModelAlias);
      set({ modelAliases: aliases, modelChoices: opt.choices });
      /* 골라 둔 것이 목록에서 사라지면(설정이 바뀌었다) 고른 것을 놓는다 — 없는 것을 보낼 순 없다. */
      const cur = get().model;
      if (cur && !aliases.includes(cur)) set({ model: null });
      const ck = get().choiceKey;
      if (ck && opt.choices !== null && !opt.choices.some((c) => c.key === ck)) set({ choiceKey: null });
    } catch {
      /* 못 읽으면 고르기를 안 그린다 — 지금까지처럼 배포의 전역 선택이 답한다. */
      set({ modelAliases: [], modelChoices: null });
    }
  },

  setModel: (m) => set({ model: m }),

  /** 다시 누르면 «안 고름» — 그러면 회사 기본이 답한다. */
  setChoiceKey: (k) => set({ choiceKey: k }),

  setAgent: (a) => {
    if (get().agent?.id === a?.id) return;
    get().abort?.();
    /* 오간 말이 있으면 새 대화로 — 에이전트는 대화에 묶이기 때문이다. */
    set({
      agent: a,
      conversationId: null,
      messages: [],
      streaming: false,
      waiting: false,
      abort: null,
    });
  },

  open: async (id) => {
    get().abort?.();
    const detail = await getAiConversation(id);
    set({
      conversationId: detail.id,
      messages: detail.messages.map((m) => ({ id: m.id, role: m.role, text: m.text })),
      streaming: false,
      waiting: false,
      abort: null,
      agent: null,
    });
    /* 그 대화를 이끌던 에이전트를 되살린다 — 이어 말하면 같은 역할이어야 한다.
       ★ 그새 중지됐으면 못 읽는다(404) — 그러면 «없이» 이어 간다(서버도 같은 판단을 한다). */
    if (detail.agentId) {
      try {
        const agent = await getAiAgent(detail.agentId);
        /* 그새 다른 대화로 옮겨 갔으면 덮지 않는다. */
        if (get().conversationId === detail.id) set({ agent });
      } catch { /* 없어졌거나 중지됐다 — 없이 이어 간다 */ }
    }
  },

  remove: async (id) => {
    await deleteAiConversation(id);
    set((s) => ({ conversations: s.conversations.filter((c) => c.id !== id) }));
    /* 지금 보던 대화를 지웠으면 화면도 비운다 — 없는 대화에 이어 말하면 안 된다. */
    if (get().conversationId === id) {
      set({ conversationId: null, messages: [] });
    }
  },

  rename: async (id, title) => {
    const clean = title.replace(/\s+/g, ' ').trim();
    if (!clean) return;
    const saved = await renameAiConversation(id, clean);
    set({
      conversations: get().conversations.map((c) => (c.id === id ? { ...c, title: saved } : c)),
    });
  },
}));
